<template>
  <v-layout>
    <v-flex>
      <v-card>
        <v-card-title>{{ $t('tsReportList') }}</v-card-title>
        <div class="px-3 py-2">
          <div class="d-flex flex-row align-start flex-wrap">
            <div class="mr-2">
              <div class="datepicker-label mt-n2">{{ $t('from') }}</div>
              <date-time-picker v-model="from" date-id="ca73b50a" time-id="d0cc30a8" />
            </div>
            <div class="mr-2">
              <div class="datepicker-label mt-n2">{{ $t('to') }}</div>
              <date-time-picker v-model="to" date-id="d34902ac" time-id="d80f3ed2" />
            </div>
            <div class="mr-2 tsResponder">
              <v-text-field id="ccb05c16" v-model="tsResponder" :label="$t('responderName')" single-line hide-details></v-text-field>
            </div>
            <div class="mr-3">
              <v-btn id="dc7db8f4" color="primary" @click="searchTsReport">
                <v-icon left>mdi-magnify</v-icon>
                {{ $t('search') }}
              </v-btn>
            </div>
            <div class="mr-3">
              <v-btn id="b2d4050c" @click="resetSearchAreas">{{ $t('reset') }}</v-btn>
            </div>
            <div>
              <v-btn id="3061ff19" color="green" @click="showExportAndOptionalDeleteDialog">
                <v-icon left>mdi-file-export-outline</v-icon>
                {{ $t('export') }}
              </v-btn>
            </div>
          </div>
          <v-data-table
            id="f115061a"
            must-sort
            :headers="headers"
            :items="tsReportList"
            :options.sync="pagination"
            :server-items-length="totalItems"
            :loading="loading"
            :loading-text="$t('dataTable.loadingPleaseWait')"
            :no-data-text="$t('dataTable.noDataAvailable')"
            :no-results-text="$t('dataTable.noMatchingRecordsFound')"
            :footer-props="{
              'items-per-page-text': this.$t('dataFooter.itemsPerPage'),
              'page-text': this.$t('dataFooter.pageText'),
              'items-per-page-options': [10,25,50]
            }"
          >
            <template v-slot:item.count="{ item }">
              <span>{{ new Intl.NumberFormat().format(item.count) }}</span>
            </template>
          </v-data-table>
        </div>
      </v-card>

      <!-- confirmationDialog -->
      <v-row justify="center">
        <v-dialog v-model="exportAndOptionalDeleteDialog" persistent max-width="600">
          <v-card id="9a0da934">
            <v-card-title class="headline green">
              <v-icon id="7e4ad7c5" left large>mdi-file-export-outline</v-icon>
              {{ $t('export') }}
            </v-card-title>
            <v-form ref="exportAndOptionalDeleteDialogForm" v-model="exportAndOptionalDeleteDialogFormValid">
              <v-card-text>
                <p class="subtitle-2 pt-3">{{ $t('dialog.text.atTheMomentResults') }}</p>
                <v-checkbox id="d394c1a2" v-model="exportAndOptionalDeleteDialogConfirmationCheckbox" class="subtitle-2 pt-3" :label="$t('dialog.text.delete.selectedItems')"></v-checkbox>
                <div v-if="exportAndOptionalDeleteDialogConfirmationCheckbox">
                  <p class="subtitle-2">{{ $t('dialog.text.warning.deletionTimeMayTakeSomeTime') }}</p>
                  <p class="subtitle-2">{{ $t('dialog.text.warning.deletionConfirmByTyping') }}</p>
                  <v-text-field id="6ae5f343" v-model="deletionConfirmText" :label="`DELETE *`" :rules="[rules.required, rules.deletionConfirmTextMatch]" required></v-text-field>
                  <small>*{{ $t('indicatesRequiredField') }}</small>
                </div>
              </v-card-text>
            </v-form>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-3" text @click="exportAndOptionalDeleteDialog = false">{{ $t('close') }}</v-btn>
              <v-btn id="5a81c2fd" class="primary" :disabled="exportAndOptionalDeleteDialogConfirmationCheckbox && !exportAndOptionalDeleteDialogFormValid"
                     :loading="dialogExportBtnLoading" @click="exportTsReport(exportAndOptionalDeleteDialogConfirmationCheckbox)"
              >
                {{ $t('export') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </v-flex>
  </v-layout>
</template>

<script>
import report from '../../api/report'
import { showToastError, showToastSuccess } from '../../event-bus'
import DateTimePicker from '../../components/DateTimePicker.vue'
import moment from 'moment'
import FileSaver from 'file-saver'

export default {
  name: 'TsReportList',

  components: {
    'date-time-picker': DateTimePicker
  },

  data() {
    return {
      from: null,
      to: null,
      tsResponder: null,
      pagination: {
        sortBy: ['responderName']
      },
      tsReportList: [],
      totalItems: 0,
      loading: false,
      exportAndOptionalDeleteDialog: false,
      exportAndOptionalDeleteDialogConfirmationCheckbox: false,
      exportAndOptionalDeleteDialogFormValid: false,
      validDeletionConfirmText: 'DELETE',
      deletionConfirmText: null,
      dialogExportBtnLoading: false,
      headers: [
        {
          text: this.$t('responderName'),
          value: 'responderName'
        },
        {
          text: this.$t('count'),
          align: 'left',
          value: 'count'
        }
      ],
      rules: {
        required: v => !!v || this.$t('validation.requiredField'),
        deletionConfirmTextMatch: v => (v === this.validDeletionConfirmText) || this.$t('validation.keywordMustBeIdentical')
      }
    }
  },

  watch: {
    pagination() {
      this.searchTsReport()
    }
  },

  methods: {
    async searchTsReport() {
      this.loading = true

      let pageRequest = {
        'page-num': this.pagination.page,
        'page-size': this.pagination.itemsPerPage,
        'sort-field': this.pagination.sortBy[0],
        'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
      }

      let q = {
        'from': this.from == null || this.from === undefined ? null : this.from,
        'to': this.to == null || this.to === undefined ? null : this.to,
        'responderName': this.tsResponder == null ? null : this.tsResponder
      }

      try {
        let page = await report.listTsReports(q, pageRequest)
        this.tsReportList = page.content
        this.totalItems = page.total
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      } finally {
        this.loading = false
      }
    },

    showExportAndOptionalDeleteDialog() {
      this.exportAndOptionalDeleteDialog = true
      this.$nextTick(() => {
        this.$refs.exportAndOptionalDeleteDialogForm.reset()
      })
    },

    async exportTsReport(useDelete) {
      this.dialogExportBtnLoading = true

      if (!this.$refs.exportAndOptionalDeleteDialogForm.validate()) {
        return
      }

      let q = {
        'from': this.from == null || this.from === undefined ? null : this.from,
        'to': this.to == null || this.to === undefined ? null : this.to,
        'responderName': this.tsResponder == null ? null : this.tsResponder
      }

      try {
        await FileSaver.saveAs(await report.exportTsReportFile(useDelete, q), moment(new Date()).format('YYYYMMDD-HHmmss') + '-exportedTsReports.csv')
        if (useDelete) {
          this.searchTsReport()
        }
        showToastSuccess(this.$t('toastMessage.succesfullyExported'))
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      } finally {
        this.exportAndOptionalDeleteDialog = false
        this.dialogExportBtnLoading = false
      }
    },

    resetSearchAreas() {
      this.from = null
      this.to = null
      this.tsResponder = null
    }
  }
}
</script>

<style>
.datepicker-label {
  font-size: 10pt;
}

.datetimepicker .v-text-field {
  padding-top: 0px;
  padding-bottom: 0px;
}

.tsResponder {
  width: 250pt;
}
</style>
